/**
 * Agent Library - Browse and manage agent configurations
 */

import React, { useState, useEffect, useRef } from 'react';
import { AgentConfig } from '../types/agent';
import { apiClient, slugifyAgentName } from '../api/client';
import { AgentBuilder, AgentBuilderTab } from './AgentBuilder';
import { downloadAgentExport, parseAgentExportFile, dedupeSlug } from '../utils/agentExport';
import { groupAgentsBySlug } from '../utils/agentVersions';
import { DownloadIcon, DuplicateIcon, PencilIcon, PlayIcon, PlusIcon, TrashIcon, UploadIcon } from './icons';
import {
  CollectionToolbar,
  SortableHeader,
  SortOption,
  compareValues,
  stopRowClick,
  useCollectionPrefs,
} from './CollectionToolbar';
import { formatRelative, formatTimestamp, timestampValue } from '../utils/formatDate';
import type { ConnectedInputInfo, UpstreamStep } from '../utils/outputPath';
import './AgentLibrary.css';

// Default agents only point at a built-in task - their prompt lives in code, not in the
// agent's config, so there's nothing to edit and a duplicate would open empty.
const DEFAULT_AGENT_NO_DUPLICATE = 'Default agents have no editable prompt to duplicate';
const DEFAULT_AGENT_NO_EDIT = 'Default agents cannot be edited';

type AgentSortKey = 'name' | 'updated' | 'created' | 'task_type' | 'model' | 'version' | 'usage';

const AGENT_SORTS: SortOption<AgentSortKey>[] = [
  { key: 'updated', label: 'Last saved', defaultDirection: 'desc' },
  { key: 'name', label: 'Name', defaultDirection: 'asc' },
  { key: 'created', label: 'Created', defaultDirection: 'desc' },
  { key: 'task_type', label: 'Task type', defaultDirection: 'asc' },
  { key: 'model', label: 'Model', defaultDirection: 'asc' },
  { key: 'version', label: 'Version', defaultDirection: 'desc' },
  { key: 'usage', label: 'Usage', defaultDirection: 'desc' },
];

const agentSortValue = (agent: AgentConfig, key: AgentSortKey) => {
  switch (key) {
    case 'name':
      return agent.name;
    case 'updated':
      return timestampValue(agent.updated_at);
    case 'created':
      return timestampValue(agent.created_at);
    case 'task_type':
      return agent.task_type;
    case 'model':
      return agent.model?.model_name;
    case 'version':
      return parseInt(agent.version, 10) || 0;
    case 'usage':
      return agent.usage_count || 0;
  }
};

interface AgentLibraryProps {
  onSelectAgent?: (agent: AgentConfig) => void;
  selectedAgentId?: string;
  initialMode?: ViewMode;
  initialAgentId?: string;
  // Fired when an in-place edit (viewMode 'edit') saves - the saved agent may
  // carry a new id/version (see create_agent_version), distinct from the
  // pre-edit id the caller opened the panel with. Lets callers that pinned
  // an agent_id (e.g. NodeConfigSidebar) follow the edit to its new version
  // instead of continuing to reference the now-superseded row.
  onAgentUpdated?: (agent: AgentConfig) => void;
  // Upstream workflow steps, when opened from a workflow node (see AgentBuilder).
  upstreamSteps?: UpstreamStep[];
  connectedInputs?: ConnectedInputInfo[];
}

type ViewMode = 'library' | 'builder' | 'edit' | 'sandbox';

// Dedupe by slug so header counts match the number of cards shown, not the
// number of underlying (slug, version) rows.
const countUniqueAgents = (list: AgentConfig[]): number =>
  new Set(list.map(a => a.slug || a.id)).size;

// Load persisted filters from localStorage
const loadPersistedFilters = () => {
  try {
    const saved = localStorage.getItem('raiqon_agent_filters');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load agent filters:', e);
  }
  return {
    searchQuery: '',
    selectedCategory: null,
    ownershipFilter: 'all',
  };
};

// Save filters to localStorage
const savePersistedFilters = (searchQuery: string, selectedCategory: string | null, ownershipFilter: string) => {
  try {
    localStorage.setItem('raiqon_agent_filters', JSON.stringify({
      searchQuery,
      selectedCategory,
      ownershipFilter,
    }));
  } catch (e) {
    console.error('Failed to save agent filters:', e);
  }
};

export const AgentLibrary: React.FC<AgentLibraryProps> = ({
  onSelectAgent,
  selectedAgentId,
  initialMode,
  initialAgentId,
  onAgentUpdated,
  upstreamSteps,
  connectedInputs,
}) => {
  const persistedFilters = loadPersistedFilters();
  const [agents, setAgents] = useState<Record<string, AgentConfig[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(initialMode ?? 'library');
  const [editingAgentId, setEditingAgentId] = useState<string | undefined>(initialAgentId);
  // Full config of the agent being duplicated - opened in the builder as an unsaved new agent.
  const [duplicateSource, setDuplicateSource] = useState<AgentConfig | undefined>(undefined);
  // Which editor tab an agent opens on: the card -> Basic Info, pencil -> Prompt & Context,
  // play -> Sandbox (initialMode 'sandbox' from other views lands there too).
  const [editingTab, setEditingTab] = useState<AgentBuilderTab>(
    initialMode === 'sandbox' ? 'sandbox' : initialMode === 'edit' ? 'prompt' : 'basic'
  );
  const [searchQuery, setSearchQuery] = useState(persistedFilters.searchQuery);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(persistedFilters.selectedCategory);
  const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'default' | 'mine'>(persistedFilters.ownershipFilter);
  const [deleteMode, setDeleteMode] = useState(false);
  const [agentsToDelete, setAgentsToDelete] = useState<Set<string>>(new Set());
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [prefs, setPrefs] = useCollectionPrefs<AgentSortKey>(
    'raiqon_agent_view',
    { view: 'grid', sortKey: 'updated', sortDirection: 'desc' },
    AGENT_SORTS.map(o => o.key)
  );
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>('');
  const [modelFilter, setModelFilter] = useState<string>('');

  useEffect(() => {
    loadAgents();
  }, []);

  // Persist filters when they change
  useEffect(() => {
    savePersistedFilters(searchQuery, selectedCategory, ownershipFilter);
  }, [searchQuery, selectedCategory, ownershipFilter]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const agentsByCategory = await apiClient.getAgentsByCategory();
      setAgents(agentsByCategory);
    } catch (err) {
      setError(`Failed to load agents: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingAgentId(undefined);
    setDuplicateSource(undefined);
    setViewMode('builder');
  };

  const handleDuplicate = async (agentId: string) => {
    setError(null);
    try {
      // listAgents() rows are summaries with placeholder prompt/context - fetch the full config.
      setDuplicateSource(await apiClient.getAgent(agentId));
      setEditingAgentId(undefined);
      setEditingTab('basic');
      setViewMode('builder');
    } catch (err: any) {
      setError(`Failed to duplicate agent: ${err.response?.data?.detail ?? err.message ?? err}`);
    }
  };

  const openAgent = (agentId: string, tab: AgentBuilderTab) => {
    setEditingAgentId(agentId);
    setEditingTab(tab);
    setViewMode('edit');
  };

  const handleEdit = (agentId: string) => openAgent(agentId, 'prompt');

  const handleTest = (agentId: string) => openAgent(agentId, 'sandbox');

  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setAgentsToDelete(new Set());
  };

  const toggleAgentForDeletion = (agentId: string) => {
    const newSet = new Set(agentsToDelete);
    if (newSet.has(agentId)) {
      newSet.delete(agentId);
    } else {
      newSet.add(agentId);
    }
    setAgentsToDelete(newSet);
  };

  const handleDeleteSelected = async () => {
    if (agentsToDelete.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${agentsToDelete.size} agent(s)?`)) {
      return;
    }

    const ids = Array.from(agentsToDelete);
    const allAgents = Object.values(agents).flat();

    try {
      setLoading(true);
      const results = await Promise.allSettled(ids.map(id => apiClient.deleteAgent(id)));

      const failures = results
        .map((result, i) => ({ result, id: ids[i] }))
        .filter((r): r is { result: PromiseRejectedResult; id: string } => r.result.status === 'rejected');

      if (failures.length > 0) {
        const reasons = failures.map(({ result, id }) => {
          const name = allAgents.find(a => a.id === id)?.name ?? id;
          const detail = result.reason?.response?.data?.detail;
          return `${name}: ${detail ?? result.reason}`;
        });
        setError(`Failed to delete ${failures.length} of ${ids.length} agent(s):\n${reasons.join('\n')}`);
      } else {
        setError(null);
      }

      // Refresh regardless of partial failure so the UI reflects whatever did succeed.
      await loadAgents();
      setAgentsToDelete(new Set(failures.map(f => f.id)));
      if (failures.length === 0) {
        setDeleteMode(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (agentId: string) => {
    setError(null);
    setSuccessMessage(null);
    setExportingId(agentId);
    try {
      const fullConfig = await apiClient.getAgent(agentId);
      downloadAgentExport(fullConfig);
    } catch (err: any) {
      setError(`Failed to export agent: ${err.response?.data?.detail ?? err}`);
    } finally {
      setExportingId(null);
    }
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError(null);
    setSuccessMessage(null);

    try {
      if (!file) return;
      const raw = await file.text();
      const imported = parseAgentExportFile(raw);

      const existingSlugs = new Set(Object.values(agents).flat().map(a => a.slug).filter((s): s is string => !!s));
      const baseSlug = slugifyAgentName(imported.slug || imported.name);
      const newSlug = dedupeSlug(baseSlug, existingSlugs);

      const {
        id: _id,
        created_at: _createdAt,
        updated_at: _updatedAt,
        created_by: _createdBy,
        usage_count: _usageCount,
        ...rest
      } = imported;
      const payload: Partial<AgentConfig> = {
        ...rest,
        slug: newSlug,
        is_default: false,
      };

      await apiClient.createAgent(payload);
      await loadAgents();
      setSuccessMessage(`Imported "${imported.name}" as "${newSlug}"`);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d: any) => d.msg ?? JSON.stringify(d)).join('; ')
        : (detail ?? err.message ?? err);
      setError(`Failed to import agent: ${message}`);
    } finally {
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setViewMode('library');
    setEditingAgentId(undefined);
    setDuplicateSource(undefined);
    await loadAgents();
  };

  // Fired when AgentBuilder saves an existing agent in place (no navigation).
  // Refreshes the cached agent list so the library's card reflects the edit
  // once the user does navigate back, and lets callers that pinned the
  // pre-edit agent_id (e.g. NodeConfigSidebar) follow the edit to its new id.
  const handleUpdate = async (agent: AgentConfig) => {
    setEditingAgentId(agent.id);
    onAgentUpdated?.(agent);
    await loadAgents();
  };

  const handleCancel = () => {
    setViewMode('library');
    setEditingAgentId(undefined);
    setDuplicateSource(undefined);
  };


  const filterAgents = (agents: Record<string, AgentConfig[]>) => {
    let filtered = { ...agents };

    // Filter by ownership (default/mine/all)
    if (ownershipFilter !== 'all') {
      const newFiltered: Record<string, AgentConfig[]> = {};
      Object.entries(filtered).forEach(([category, categoryAgents]) => {
        const matchingAgents = categoryAgents.filter(agent => {
          if (ownershipFilter === 'default') {
            return agent.is_default === true;
          } else if (ownershipFilter === 'mine') {
            return agent.is_default === false;
          }
          return true;
        });
        if (matchingAgents.length > 0) {
          newFiltered[category] = matchingAgents;
        }
      });
      filtered = newFiltered;
    }

    // Filter by category
    if (selectedCategory) {
      filtered = { [selectedCategory]: filtered[selectedCategory] || [] };
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const newFiltered: Record<string, AgentConfig[]> = {};

      Object.entries(filtered).forEach(([category, categoryAgents]) => {
        const matchingAgents = categoryAgents.filter(agent =>
          agent.name.toLowerCase().includes(query) ||
          agent.description.toLowerCase().includes(query) ||
          (agent.tags && agent.tags.some(tag => tag.toLowerCase().includes(query)))
        );

        if (matchingAgents.length > 0) {
          newFiltered[category] = matchingAgents;
        }
      });

      filtered = newFiltered;
    }

    return filtered;
  };

  if (viewMode === 'builder' || viewMode === 'edit' || viewMode === 'sandbox') {
    return (
      <AgentBuilder
        key={`${editingAgentId ?? duplicateSource?.id ?? 'new'}:${viewMode === 'builder' ? 'basic' : editingTab}`}
        agentId={editingAgentId}
        initialTab={viewMode === 'builder' ? 'basic' : editingTab}
        templateAgent={viewMode === 'builder' ? duplicateSource : undefined}
        upstreamSteps={upstreamSteps}
        connectedInputs={connectedInputs}
        knownCategories={Object.keys(agents).sort()}
        knownTags={Array.from(new Set(Object.values(agents).flat().flatMap(a => a.tags || []))).sort()}
        existingSlugs={new Set(Object.values(agents).flat().map(a => a.slug).filter((s): s is string => !!s))}
        onSave={handleSave}
        onUpdate={handleUpdate}
        onCancel={handleCancel}
      />
    );
  }

  const filteredAgents = filterAgents(agents);
  const allAgents = Object.values(filteredAgents).flat();
  // One entry per slug (its newest version), narrowed by task type / model, then sorted.
  const agentGroups = groupAgentsBySlug(allAgents)
    .filter(group => !taskTypeFilter || group.versions[0].task_type === taskTypeFilter)
    .filter(group => !modelFilter || group.versions[0].model?.model_name === modelFilter)
    .sort((a, b) =>
      compareValues(agentSortValue(a.versions[0], prefs.sortKey), agentSortValue(b.versions[0], prefs.sortKey), prefs.sortDirection)
    );
  const totalAgents = agentGroups.length;
  const unfilteredTotal = countUniqueAgents(Object.values(agents).flat());

  const flatAgents = Object.values(agents).flat();
  const taskTypes = Array.from(new Set(flatAgents.map(a => a.task_type).filter(Boolean))).sort() as string[];
  const modelNames = Array.from(new Set(flatAgents.map(a => a.model?.model_name).filter(Boolean))).sort() as string[];

  const openAgentFromList = (agent: AgentConfig) => {
    if (deleteMode) toggleAgentForDeletion(agent.id);
    // Picker mode (AgentPanel) selects the agent; the standalone library opens it.
    else if (onSelectAgent) onSelectAgent(agent);
    else openAgent(agent.id, 'basic');
  };

  return (
    <div className="agent-library">
      <div className="agent-library-header">
        <div className="header-top">
          <h2>Agents</h2>
          <div className="header-actions">
            {deleteMode ? (
              <>
                <button onClick={toggleDeleteMode} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="btn-danger"
                  disabled={agentsToDelete.size === 0}
                >
                  Delete ({agentsToDelete.size})
                </button>
              </>
            ) : (
              <>
                <button onClick={toggleDeleteMode} className="btn-icon-large" title="Delete agents">
                  <TrashIcon size={16} />
                </button>
                <button onClick={handleImportClick} className="btn-secondary" title="Import an agent from a JSON export">
                  <UploadIcon /> Import
                </button>
                <input
                  type="file"
                  accept="application/json"
                  ref={importInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImportFile}
                />
                <button onClick={handleCreateNew} className="btn-primary">
                  <PlusIcon /> New Agent
                </button>
              </>
            )}
          </div>
        </div>

        <div className="header-filters">
          <div className="filter-buttons ownership-filter">
            <button
              className={`filter-btn ${ownershipFilter === 'all' ? 'active' : ''}`}
              onClick={() => setOwnershipFilter('all')}
            >
              All ({countUniqueAgents(Object.values(agents).flat())})
            </button>
            <button
              className={`filter-btn ${ownershipFilter === 'default' ? 'active' : ''}`}
              onClick={() => setOwnershipFilter('default')}
            >
              Default ({countUniqueAgents(Object.values(agents).flat().filter(a => a.is_default))})
            </button>
            <button
              className={`filter-btn ${ownershipFilter === 'mine' ? 'active' : ''}`}
              onClick={() => setOwnershipFilter('mine')}
            >
              My Agents ({countUniqueAgents(Object.values(agents).flat().filter(a => !a.is_default))})
            </button>
          </div>

          <div className="filter-buttons category-chips">
            <button
              className={`filter-btn ${selectedCategory === null ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              All
            </button>
            {Object.keys(agents).sort().map(category => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
                <span className="filter-chip-count">{countUniqueAgents(agents[category])}</span>
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />

          <div className="agent-filter-selects">
            <select value={taskTypeFilter} onChange={(e) => setTaskTypeFilter(e.target.value)} aria-label="Task type">
              <option value="">All task types</option>
              {taskTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} aria-label="Model">
              <option value="">All models</option>
              {modelNames.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            {(taskTypeFilter || modelFilter) && (
              <button
                className="agent-filter-clear"
                onClick={() => {
                  setTaskTypeFilter('');
                  setModelFilter('');
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ whiteSpace: 'pre-line' }}>{error}</div>
        )}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      <div className="agent-library-content">
        {loading ? (
          <div className="loading-state">Loading agents...</div>
        ) : unfilteredTotal === 0 ? (
          <div className="empty-state">
            <p>No agents found.</p>
            <button onClick={handleCreateNew} className="btn-primary">
              Create Your First Agent
            </button>
          </div>
        ) : (
          <div className="agents-container">
            <CollectionToolbar
              prefs={prefs}
              onChange={setPrefs}
              sortOptions={AGENT_SORTS}
              resultCount={totalAgents}
              totalCount={unfilteredTotal}
              noun="agents"
            />
            {totalAgents === 0 ? (
              <div className="empty-state">
                <p>No agents match the current search and filters.</p>
              </div>
            ) : prefs.view === 'list' ? (
              <div className="collection-table-wrap">
                <table className="collection-table">
                  <thead>
                    <tr>
                      {deleteMode && <th className="collection-table__check" />}
                      <SortableHeader label="Name" sortKey="name" prefs={prefs} sortOptions={AGENT_SORTS} onChange={setPrefs} />
                      <SortableHeader label="Task type" sortKey="task_type" prefs={prefs} sortOptions={AGENT_SORTS} onChange={setPrefs} />
                      <SortableHeader label="Model" sortKey="model" prefs={prefs} sortOptions={AGENT_SORTS} onChange={setPrefs} />
                      <SortableHeader label="Version" sortKey="version" prefs={prefs} sortOptions={AGENT_SORTS} onChange={setPrefs} />
                      <th>Tags</th>
                      <SortableHeader label="Last saved" sortKey="updated" prefs={prefs} sortOptions={AGENT_SORTS} onChange={setPrefs} />
                      {!deleteMode && <th className="collection-table__actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {agentGroups.map(group => {
                      const agent = group.versions[0];
                      return (
                        <tr
                          key={group.key}
                          className={`${selectedAgentId === agent.id ? 'is-selected' : ''} ${agentsToDelete.has(agent.id) ? 'is-marked' : ''}`}
                          onClick={() => openAgentFromList(agent)}
                        >
                          {deleteMode && (
                            <td className="collection-table__check">
                              <input
                                type="checkbox"
                                checked={agentsToDelete.has(agent.id)}
                                onChange={() => toggleAgentForDeletion(agent.id)}
                                onClick={stopRowClick}
                              />
                            </td>
                          )}
                          <td>
                            <span className="collection-table__name">{agent.name}</span>
                            {agent.is_default && <span className="collection-table__badge" style={{ marginLeft: 6 }}>Default</span>}
                            {agent.description && <span className="collection-table__sub">{agent.description}</span>}
                          </td>
                          <td className="collection-table__muted">{agent.task_type}</td>
                          <td className="collection-table__muted">{agent.model?.model_name || '—'}</td>
                          <td className="collection-table__muted">
                            v{agent.version}
                            {group.versions.length > 1 && ` (${group.versions.length} versions)`}
                          </td>
                          <td>
                            <div className="collection-table__tags">
                              {(agent.tags || []).map(tag => (
                                <span key={tag} className="collection-table__tag">{tag}</span>
                              ))}
                            </div>
                          </td>
                          <td className="collection-table__muted" title={formatTimestamp(agent.updated_at)}>
                            {formatRelative(agent.updated_at)}
                          </td>
                          {!deleteMode && (
                            <td className="collection-table__actions" onClick={stopRowClick}>
                              <div className="collection-table__actions-inner">
                                {!onSelectAgent && (
                                  <button className="collection-table__action" onClick={() => handleTest(agent.id)} title="Open in sandbox">
                                    <PlayIcon />
                                  </button>
                                )}
                                <button
                                  className="collection-table__action"
                                  onClick={() => handleEdit(agent.id)}
                                  disabled={agent.is_default}
                                  title={agent.is_default ? DEFAULT_AGENT_NO_EDIT : 'Edit prompt & context'}
                                >
                                  <PencilIcon />
                                </button>
                                <button
                                  className="collection-table__action"
                                  onClick={() => handleDuplicate(agent.id)}
                                  disabled={agent.is_default}
                                  title={agent.is_default ? DEFAULT_AGENT_NO_DUPLICATE : 'Duplicate agent'}
                                >
                                  <DuplicateIcon />
                                </button>
                                {!agent.is_default && (
                                  <button
                                    className="collection-table__action"
                                    onClick={() => handleExport(agent.id)}
                                    disabled={exportingId === agent.id}
                                    title="Export agent as JSON"
                                  >
                                    <DownloadIcon />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className="agents-grid">
              {agentGroups.map(group => {
                    const agent = group.versions[0];
                    return (
                    <div
                      key={group.key}
                      className={`agent-card ${selectedAgentId === agent.id ? 'selected' : ''} ${deleteMode ? 'delete-mode' : ''} ${agentsToDelete.has(agent.id) ? 'marked-for-deletion' : ''}`}
                      onClick={() => openAgentFromList(agent)}
                    >
                      {deleteMode && (
                        <div className="delete-checkbox">
                          <input
                            type="checkbox"
                            checked={agentsToDelete.has(agent.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleAgentForDeletion(agent.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <div className="agent-card-header">
                        <h4>{agent.name}</h4>
                        {!deleteMode && (
                          <div className="agent-card-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleTest(agent.id)}
                              className="btn-icon"
                              title="Test agent"
                            >
                              <PlayIcon />
                            </button>
                            <button
                              onClick={() => handleEdit(agent.id)}
                              className="btn-icon"
                              disabled={agent.is_default}
                              title={agent.is_default ? DEFAULT_AGENT_NO_EDIT : 'Edit agent'}
                            >
                              <PencilIcon />
                            </button>
                            <button
                              onClick={() => handleDuplicate(agent.id)}
                              className="btn-icon"
                              disabled={agent.is_default}
                              title={agent.is_default ? DEFAULT_AGENT_NO_DUPLICATE : 'Duplicate agent'}
                            >
                              <DuplicateIcon />
                            </button>
                            {!agent.is_default && (
                              <button
                                onClick={() => handleExport(agent.id)}
                                className="btn-icon"
                                title="Export agent as JSON"
                                disabled={exportingId === agent.id}
                              >
                                <DownloadIcon />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <p className="agent-description">{agent.description}</p>

                      <div className="agent-meta">
                        {agent.model?.model_name && (
                          <div className="agent-model">
                            <span className="label">Model:</span>
                            <span className="value">{agent.model.model_name}</span>
                          </div>
                        )}
                        <div className="agent-version">
                          <span className="label">Version:</span>
                          <span className="value">{agent.version}</span>
                        </div>
                      </div>

                      {agent.tags && agent.tags.length > 0 && (
                        <div className="agent-tags">
                          {agent.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="tag">{tag}</span>
                          ))}
                          {agent.tags.length > 3 && (
                            <span className="tag tag-more">+{agent.tags.length - 3}</span>
                          )}
                        </div>
                      )}

                      {agent.usage_count > 0 && (
                        <div className="agent-usage">
                          Used {agent.usage_count} time{agent.usage_count !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    );
                  })}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
